'use client'

import { useCallback, useMemo } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

function parseIdList(param: string | null): number[] {
  if (!param || !param.trim()) return []
  return param
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)
}

export function useServiceFilters() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const categoryIds = useMemo(
    () => parseIdList(searchParams.get('category')),
    [searchParams]
  )
  const locationIds = useMemo(
    () => parseIdList(searchParams.get('location')),
    [searchParams]
  )
  const highlightedOnly = useMemo(
    () => searchParams.get('highlighted') === '1',
    [searchParams]
  )

  const setFilters = useCallback(
    (
      nextCategoryIds: number[],
      nextLocationIds: number[],
      nextHighlightedOnly: boolean
    ) => {
      const p = new URLSearchParams(searchParams.toString())
      if (nextCategoryIds.length > 0) {
        p.set('category', [...new Set(nextCategoryIds)].sort((a, b) => a - b).join(','))
      } else {
        p.delete('category')
      }
      if (nextLocationIds.length > 0) {
        p.set('location', [...new Set(nextLocationIds)].sort((a, b) => a - b).join(','))
      } else {
        p.delete('location')
      }
      if (nextHighlightedOnly) {
        p.set('highlighted', '1')
      } else {
        p.delete('highlighted')
      }
      const q = p.toString()
      router.replace(q ? `${pathname}?${q}` : pathname, { scroll: false })
    },
    [searchParams, router, pathname]
  )

  const toggleCategory = useCallback(
    (id: number) => {
      const set = new Set(categoryIds)
      if (set.has(id)) set.delete(id)
      else set.add(id)
      setFilters(Array.from(set), locationIds, highlightedOnly)
    },
    [categoryIds, locationIds, highlightedOnly, setFilters]
  )

  const toggleLocation = useCallback(
    (id: number) => {
      const set = new Set(locationIds)
      if (set.has(id)) set.delete(id)
      else set.add(id)
      setFilters(categoryIds, Array.from(set), highlightedOnly)
    },
    [categoryIds, locationIds, highlightedOnly, setFilters]
  )

  const toggleHighlighted = useCallback(() => {
    setFilters(categoryIds, locationIds, !highlightedOnly)
  }, [categoryIds, locationIds, highlightedOnly, setFilters])

  const clearFilters = useCallback(() => {
    setFilters([], [], false)
  }, [setFilters])

  return {
    categoryIds,
    locationIds,
    highlightedOnly,
    setFilters,
    toggleCategory,
    toggleLocation,
    toggleHighlighted,
    clearFilters,
  }
}
